import { type FC, useMemo } from "react";
import {
  BarChart3,
  DollarSign,
  EyeOff,
  Gauge,
  LineChart,
  Receipt,
  Users,
  Wallet,
} from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DashboardKpiCard, type DefinitionTooltipInfo } from "src/components/common";
import type {
  SalesAttributionKpiEntry,
  SalesAttributionKpiKey,
  SalesAttributionKpis,
} from "../utils/salesAttributionTypes";

const kpiTooltip = (
  t: TFunction,
  key: string,
  descriptionDefault: string,
  formulaDefault: string
): DefinitionTooltipInfo => ({
  description: t(`salesAttribution.tooltips.kpi.${key}.description`, {
    defaultValue: descriptionDefault,
  }),
  formula: t(`salesAttribution.tooltips.kpi.${key}.formula`, {
    defaultValue: formulaDefault,
  }),
});

interface SalesAttributionKpiSectionProps {
  kpis: SalesAttributionKpis;
}

const ICON_SIZE = 18;

interface KpiTitleDescriptor {
  key: SalesAttributionKpiKey;
  titleKey: string;
  titleDefault: string;
  tooltipKey: string;
  descriptionDefault: string;
  formulaDefault: string;
  icon: JSX.Element;
  lowerIsBetter?: boolean;
}

const SOURCE_KPI_DESCRIPTORS: KpiTitleDescriptor[] = [
  {
    key: "shopifyRevenue",
    titleKey: "ZidRevenue",
    titleDefault: "Zid Revenue",
    tooltipKey: "ZidRevenue",
    descriptionDefault:
      "Total revenue generated from Zid orders during the selected date range.",
    formulaDefault: "Zid revenue = sum of Zid order revenue",
    icon: <DollarSign size={ICON_SIZE} />,
  },
  {
    key: "shopifyUsers",
    titleKey: "ZidUsers",
    titleDefault: "Zid Total Users",
    tooltipKey: "ZidUsers",
    descriptionDefault:
      "Total unique visitors who visit the store during the selected date range.",
    formulaDefault: "Zid total users = count of unique store visitors",
    icon: <Users size={ICON_SIZE} />,
  },
  {
    key: "ga4Revenue",
    titleKey: "ga4Revenue",
    titleDefault: "GA4 Revenue",
    tooltipKey: "ga4Revenue",
    descriptionDefault:
      "Total purchase revenue tracked by Google Analytics 4 during the selected date range.",
    formulaDefault: "GA4 revenue = sum of GA4 purchase revenue",
    icon: <LineChart size={ICON_SIZE} />,
  },
  {
    key: "ga4Users",
    titleKey: "ga4Users",
    titleDefault: "GA4 Total Users",
    tooltipKey: "ga4Users",
    descriptionDefault:
      "Total unique users who visited your website during the selected date range, as tracked by GA4.",
    formulaDefault: "GA4 total users = count of unique GA4 users",
    icon: <Users size={ICON_SIZE} />,
  },
  {
    key: "googleRevenue",
    titleKey: "googleRevenue",
    titleDefault: "Google Ads Revenue",
    tooltipKey: "googleRevenue",
    descriptionDefault:
      "Revenue attributed to Google Ads campaigns during the selected date range.",
    formulaDefault:
      "Google Ads revenue = sum of conversion revenue attributed to Google Ads",
    icon: <BarChart3 size={ICON_SIZE} />,
  },
  {
    key: "googleSpend",
    titleKey: "googleSpend",
    titleDefault: "Google Ads Spend",
    tooltipKey: "googleSpend",
    descriptionDefault:
      "Total advertising cost incurred on Google Ads during the selected date range.",
    formulaDefault: "Google Ads spend = sum of Google Ads campaign spend",
    icon: <Wallet size={ICON_SIZE} />,
    lowerIsBetter: true,
  },
  {
    key: "metaRevenue",
    titleKey: "metaRevenue",
    titleDefault: "Meta Ads Revenue",
    tooltipKey: "metaRevenue",
    descriptionDefault:
      "Revenue attributed to Meta (Facebook & Instagram) ad campaigns during the selected date range.",
    formulaDefault: "Meta Ads revenue = sum of conversion revenue attributed to Meta Ads",
    icon: <BarChart3 size={ICON_SIZE} />,
  },
  {
    key: "metaSpend",
    titleKey: "metaSpend",
    titleDefault: "Meta Ads Spend",
    tooltipKey: "metaSpend",
    descriptionDefault:
      "Total advertising cost incurred on Meta (Facebook & Instagram) campaigns during the selected date range.",
    formulaDefault: "Meta Ads spend = sum of Meta Ads campaign spend",
    icon: <Wallet size={ICON_SIZE} />,
    lowerIsBetter: true,
  },
];

const INVISIBLE_CHANNEL_KPI_DESCRIPTORS: KpiTitleDescriptor[] = [
  {
    key: "untrackedRevenue",
    titleKey: "untrackedRevenue",
    titleDefault: "Not Tracked Revenue",
    tooltipKey: "untrackedRevenue",
    descriptionDefault: "Revenue that had no channel against them.",
    formulaDefault: "Not tracked revenue = Zid revenue - GA4 revenue",
    icon: <EyeOff size={ICON_SIZE} />,
    lowerIsBetter: true,
  },
  {
    key: "untrackedShare",
    titleKey: "untrackedShare",
    titleDefault: "Not Tracked Share",
    tooltipKey: "untrackedShare",
    descriptionDefault:
      "Untracked revenue as a share of total store revenue. Lower is better.",
    formulaDefault: "Not tracked share = not tracked revenue ÷ Zid revenue",
    icon: <Gauge size={ICON_SIZE} />,
    lowerIsBetter: true,
  },
  {
    key: "untrackedOrders",
    titleKey: "untrackedOrders",
    titleDefault: "Not Tracked Orders",
    tooltipKey: "untrackedOrders",
    descriptionDefault: "Orders implied by the untracked revenue share across this window.",
    formulaDefault: "Not tracked orders = Zid orders x not tracked share",
    icon: <Receipt size={ICON_SIZE} />,
    lowerIsBetter: true,
  },
];

export const SalesAttributionKpiSection: FC<SalesAttributionKpiSectionProps> = ({
  kpis,
}) => {
  const { t } = useTranslation();

  const tooltips = useMemo(
    () =>
      new Map(
        [...SOURCE_KPI_DESCRIPTORS, ...INVISIBLE_CHANNEL_KPI_DESCRIPTORS].map(descriptor => [
          descriptor.key,
          kpiTooltip(
            t,
            descriptor.tooltipKey,
            descriptor.descriptionDefault,
            descriptor.formulaDefault
          ),
        ])
      ),
    [t]
  );

  const notConnectedLabel = t("salesAttribution.ui.notConnected", { defaultValue: "Not connected" });
  const sourceNotConnectedLabel = t("salesAttribution.ui.sourceNotConnected", {
    defaultValue: "Source not connected for this account",
  });

  const renderCard = (descriptor: KpiTitleDescriptor) => {
    const entry: SalesAttributionKpiEntry | undefined = kpis[descriptor.key];
    const isConnected = entry?.available !== false;
    const title = t(`salesAttribution.ui.kpiTitles.${descriptor.titleKey}`, {
      defaultValue: descriptor.titleDefault,
    });

    return (
      <DashboardKpiCard
        key={descriptor.key}
        title={
          <span className="sales-attribution__kpi-title">
            {title}
            {isConnected ? null : (
              <span className="sales-attribution__kpi-badge">{notConnectedLabel}</span>
            )}
          </span>
        }
        metricLabelForTooltip={title}

        value={isConnected ? entry?.formattedValue ?? "—" : "—"}
        sublabel={isConnected ? undefined : sourceNotConnectedLabel}
        icon={descriptor.icon}
        tooltip={tooltips.get(descriptor.key)}

        changePercent={isConnected ? entry?.percentChange ?? null : null}
        comparisonLabel={t("salesAttribution.ui.vsLastPeriod", { defaultValue: "vs last period" })}
        trendPolarity={descriptor.lowerIsBetter ? "lower-is-better" : "higher-is-better"}
      />
    );
  };

  return (
    <div className="sales-attribution__kpis">
      <div className="sales-attribution__kpi-grid">{SOURCE_KPI_DESCRIPTORS.map(renderCard)}</div>

      <div className="sales-attribution__invisible-channel">
        <h2 className="sales-attribution__invisible-channel-title">
          <EyeOff size={16} aria-hidden />
          {t("salesAttribution.ui.invisibleChannel", { defaultValue: "Invisible Channel" })}
        </h2>
        <div className="sales-attribution__kpi-grid">
          {INVISIBLE_CHANNEL_KPI_DESCRIPTORS.map(renderCard)}
        </div>
      </div>
    </div>
  );
};

export default SalesAttributionKpiSection;
